// Manually authored blog posts recreating three live McKay Roofing & Solar
// articles. Every image below is an asset that already exists in this project.

import type { BlogPost } from "./blogPosts";

import solarEnergyHero from "@/assets/mckay/solar-energy.webp.asset.json";
import solarInstallationSection from "@/assets/service-heroes/solar-installation.jpg.asset.json";
import solarInstallHero from "@/assets/mckay/solar-install.webp.asset.json";
import roofReplacementSection from "@/assets/service-heroes/roof-replacement.jpg.asset.json";
import solarBatteryHero from "@/assets/service-heroes/solar-battery-installation.jpg.asset.json";
import teslaPowerwallSection from "@/assets/service-heroes/tesla-powerwall-installation.jpg.asset.json";

export const group3Posts: BlogPost[] = [
  {
    slug: "solar-cost-san-diego-2026",
    title: "What Solar Really Costs in San Diego in 2026 | McKay",
    h1: "What does solar actually cost in San Diego in 2026, now that the federal credit is gone?",
    metaDescription:
      "The 30% federal solar credit ended for homeowners after 2025. Here's what solar really costs in San Diego in 2026, what incentives remain, and how NEM 3.0 and SDG&E peak pricing change the math.",
    keywords:
      "solar cost San Diego 2026, federal solar tax credit ended, NEM 3.0 San Diego, SDG&E peak pricing, solar incentives California 2026, net billing tariff",
    category: "Solar Insights",
    datePublished: "2026-09-02",
    dateModified: "2026-09-09",
    readMinutes: 9,
    hero: {
      src: solarEnergyHero.url,
      alt: "Solar panels on a San Diego rooftop under bright afternoon sun",
      caption: "The economics of solar in San Diego changed in 2026. The panels didn't get less useful - the incentives got smaller.",
    },
    intro: [
      "If you are reading roofing and solar websites in San Diego right now, you are going to see the number 30% a lot. It is out of date. The federal residential solar tax credit that homeowners had claimed for twenty years, under Internal Revenue Code section 25D, no longer applies to expenditures made after December 31, 2025. That is not an interpretation. The statute says it directly: the credit does not apply to any expenditures made after that date [(26 U.S.C. §25D(h))](https://www.energy.gov/).",
      "We are a roofing and solar company. It would be more convenient for us if that were not true. It is true anyway, and you are going to find out eventually, so you should find out from us rather than from a contractor still running a 2024 slide deck.",
      "This post covers what changed, what is genuinely still available in 2026, why the export credit is no longer the reason to go solar in SDG&E territory, why the battery is the reason now, what solar does not remove from your bill, and how to check any incentive number a contractor, including us, puts in front of you.",
    ],
    tldr: [
      "The federal 25D credit for homeowner-owned residential solar ended for any system completed after December 31, 2025. Completion date matters, not contract date or deposit date.",
      "Under NEM 3.0 (the net billing tariff, in effect since April 15, 2023), exported solar power is credited well below retail rates, so a battery paired with solar carries most of the savings now.",
      "SDG&E's on-peak window is 4pm to 9pm. A right-sized system with storage that avoids buying power in that window usually beats an oversized panel-only array.",
      "California's property tax exclusion for solar still applies to systems completed before January 1, 2027, and SGIP battery rebates are mostly closed in SDG&E territory right now.",
    ],
    sections: [
      {
        id: "what-actually-changed",
        h2: "What actually changed with the federal solar tax credit?",
        blocks: [
          {
            type: "p",
            text: "Two things changed, and the second one is the one that catches people. First, the credit terminated. Section 25D was ended by Public Law 119-21, signed in July 2025. For a residential system you own, there is no 30% federal credit available in 2026.",
          },
          {
            type: "p",
            text: "Second, the IRS measures the deadline by completion, not by payment. This is where a lot of homeowners got hurt. The rule is that an expenditure is treated as made when the original installation of the item is completed. The IRS spelled out the consequence directly: if installation is completed after December 31, 2025, the expenditure is treated as made after that date, which prevents the taxpayer from claiming the section 25D credit [(IRS FAQs on OBBB modifications)](https://www.energy.gov/).",
          },
          {
            type: "ul",
            items: [
              "A contract signed in October 2025 did not preserve the credit.",
              "A deposit paid in December 2025 did not preserve it.",
              "A system still waiting on permit or interconnection when the calendar turned did not preserve it.",
              "Only a completed installation before January 1, 2026 qualified.",
            ],
          },
          {
            type: "callout",
            title: "Not tax advice",
            text: "If someone sold you a system in late 2025 on the promise that a deposit would lock in the credit, that is a conversation to have with a tax professional, not with a salesperson. We are not tax advisors and this page is not tax advice.",
          },
        ],
        image: {
          src: solarInstallationSection.url,
          alt: "Solar installation crew mounting panels on a residential roof",
          caption: "Completion date, not contract date, is what the IRS uses to determine eligibility for the old federal credit.",
        },
      },
      {
        id: "what-is-still-available",
        h2: "What incentives are genuinely still available in 2026?",
        blocks: [
          {
            type: "p",
            text: "Here is the honest inventory. Every item below is dated, because every one of them can move.",
          },
          { type: "h3", text: "Commercial solar, on a clock" },
          {
            type: "p",
            text: "Section 48E, the clean electricity investment credit, is alive for business-owned systems, but the timeline tightened sharply. Solar facilities that began construction after July 4, 2026 must be placed in service by December 31, 2027 to claim it. Energy storage is carved out of that solar and wind sunset. If you own a commercial building in Miramar, Kearny Mesa, or Poway, this is a real deadline and the engineering lead time is not short. Talk to your CPA before you talk to us.",
          },
          { type: "h3", text: "Lease and PPA arrangements are structurally different" },
          {
            type: "p",
            text: "With a lease or a power purchase agreement, you do not own the system, so there is nothing for you to claim directly. Any benefit reaches you only as a lower price or rate, if the provider chooses to pass it through. Ask to see the escalator, the term, the buyout, and what happens if you sell the house or need a [roof replacement](/services/roof-replacement) under the array.",
          },
          { type: "h3", text: "California's property tax exclusion, expiring soon" },
          {
            type: "p",
            text: "An active solar energy system currently does not trigger a property tax reassessment as new construction, but only for systems in progress or completed before January 1, 2027 [(California BOE guidance)](https://www.cpuc.ca.gov/). Systems excluded before the sunset stay excluded until the property changes ownership. This is one of the few genuine 2026 deadlines left, and it is a completion deadline, not a contract deadline.",
          },
          { type: "h3", text: "SGIP battery rebates, mostly closed" },
          {
            type: "p",
            text: "The Self-Generation Incentive Program is administered here by the Center for Sustainable Energy. As of the program's own metrics dated August 20, 2026, the general Small Residential Storage category shows closed, at a Step 7 rate of $0.15 per watt-hour. Higher equity rates belong to income-qualified categories, and those show closed or waitlisted as well. We are not going to quote you a battery price with an SGIP rebate subtracted out of it. If a category reopens and you qualify, we will tell you and help you file.",
          },
          {
            type: "p",
            text: "There is no California state income tax credit for residential solar. There never was one in the sense people mean.",
          },
        ],
      },
      {
        id: "nem-3-vs-nem-2",
        h2: "How does NEM 3.0 change what solar is worth in San Diego?",
        blocks: [
          {
            type: "p",
            text: "Under the net billing tariff, called NEM 3.0, which has applied to every new interconnection since April 15, 2023, the energy you send back to the grid is credited at values from the CPUC's Avoided Cost Calculator, which the CPUC itself describes as usually lower than import rates [(CPUC, Net Energy Metering and Net Billing)](https://www.cpuc.ca.gov/).",
          },
          {
            type: "p",
            text: "Practically: the kilowatt-hour your panels make at 11 a.m. and push to the grid is not worth the same as the kilowatt-hour you buy back at 6 p.m. It is worth considerably less. An oversized array that exports heavily in the middle of the day is no longer the efficient design it was under NEM 2.0.",
          },
          {
            type: "callout",
            title: "Already on NEM 2.0?",
            text: "If you already have NEM 2.0, you keep that tariff for 20 years from your interconnection date. That legacy is worth protecting, and it is one of the specific reasons to be careful about who touches your array during a reroof.",
          },
          {
            type: "quote",
            text: "Usually lower than import rates.",
            cite: "CPUC, describing net billing export credit values",
          },
        ],
      },
      {
        id: "peak-pricing-and-batteries",
        h2: "Why does a battery matter more than the panel count now?",
        blocks: [
          {
            type: "p",
            text: "SDG&E's on-peak window is 4 p.m. to 9 p.m. SDG&E's own guidance to solar customers is to try to use your battery during on-peak hours from 4pm to 9pm when electricity is priced the highest [(SDG&E Solar Billing Plan)](https://www.sdge.com/).",
          },
          {
            type: "p",
            text: "That is the whole economics of solar in San Diego in 2026, compressed into one sentence. The value is in not buying expensive evening electricity, not in selling cheap midday electricity. The CPUC reports that roughly 70% of net billing customers paired storage with their solar by the end of 2024, and that is why.",
          },
          {
            type: "p",
            text: "It also means a smaller, well-matched array with storage frequently beats a bigger array without it. We would rather sell you the right system twice, panels now, storage when it pencils, than an oversized one once. See our [solar battery installation](/services/solar-battery-installation) and [Tesla Powerwall installation](/services/tesla-powerwall-installation) pages for what that looks like in practice.",
          },
        ],
      },
      {
        id: "what-solar-does-not-remove",
        h2: "What does solar not remove from your electric bill?",
        blocks: [
          {
            type: "p",
            text: "This gets left out of a lot of proposals, so here it is plainly. SDG&E's Base Services Charge applies to residential customers including solar customers, because the house is still connected to the grid. It runs roughly $24 per month for general residential customers, about $12 for FERA/DRAH, and about $6 for CARE, effective October 2025. Solar export credits do not net against it.",
          },
          {
            type: "p",
            text: "There are also non-bypassable charges for public purpose programs that export credits cannot offset. So \"solar eliminates your electric bill\" is not a thing that happens. \"Solar substantially reduces the usage-based portion of your bill, and a battery attacks the most expensive hours\" is a thing that happens.",
          },
          {
            type: "ul",
            items: [
              "We will not give you a savings number before we have seen a full year of your bills.",
              "We will not tell you that you need a new roof when you don't - check with a [roof inspection](/services/roof-inspection) first.",
              "We will not quote a federal residential tax credit, because there isn't one for homeowner-owned systems completed in 2026.",
            ],
          },
        ],
        image: {
          src: roofReplacementSection.url,
          alt: "Roofer inspecting shingles before a solar installation",
          caption: "Mounting solar to a roof with only a few years left means paying to remove and reinstall the array later.",
        },
      },
      {
        id: "check-the-numbers",
        h2: "How do I check any incentive number a contractor gives me?",
        blocks: [
          {
            type: "p",
            text: "This is not a trick and it is not aimed at any particular competitor. Most solar salespeople in San Diego are working from slide decks that were accurate in 2024 and were never updated. Here is how to check any of it yourself, in about fifteen minutes.",
          },
          {
            type: "ol",
            items: [
              "Ask for the incentive by name and citation - not \"the tax credit,\" but the section number, the program name, or the tariff schedule.",
              "Look up whether it applies to owned systems, leased systems, or both.",
              "Check the completion deadline, not the contract deadline.",
              "Confirm your utility territory - SDG&E, or a Community Choice program like San Diego Community Power - since generation charges differ.",
              "Ask what your system's export credits are actually worth under the current tariff, not the old one.",
            ],
          },
          {
            type: "p",
            text: "If you want a straight answer on your own roof and your own bills, [contact us](/contact) and send twelve months of statements. We'll model it before we quote it. If your roof needs work first, look at [solar panel removal and reinstallation](/services/solar-panel-removal-reinstallation) and [solar installation](/services/solar-installation), and check your provider on the [utility companies](/utility-companies) page.",
          },
        ],
      },
    ],
    faqs: [
      {
        q: "Is the 30% federal solar tax credit really gone in 2026?",
        a: "For homeowner-owned residential systems, yes. Section 25D no longer applies to expenditures made after December 31, 2025, and the IRS measures that by completion date, not contract or deposit date.",
      },
      {
        q: "What is NEM 3.0 and how is it different from NEM 2.0?",
        a: "NEM 3.0, formally the net billing tariff, has applied to every new interconnection since April 15, 2023. It credits exported solar power at avoided-cost values that are usually lower than retail rates, unlike NEM 2.0's closer-to-retail export credits.",
      },
      {
        q: "Do I still need a battery if I already have solar panels?",
        a: "Under NEM 3.0, most of the financial value shifts to avoiding expensive evening electricity rather than exporting midday power. A battery paired with SDG&E's 4pm-9pm peak window is where the savings usually live now.",
      },
      {
        q: "Are SGIP battery rebates available in San Diego right now?",
        a: "The general Small Residential Storage category is closed as of the program's August 2026 metrics. Equity categories are closed or waitlisted. We do not quote a battery price assuming a rebate that isn't currently open.",
      },
      {
        q: "Will solar eliminate my SDG&E bill entirely?",
        a: "No. Fixed charges like the Base Services Charge and non-bypassable public purpose charges still apply regardless of how much solar you generate. Solar reduces the usage-based portion of your bill; it doesn't zero out the bill.",
      },
      {
        q: "What is the property tax exclusion deadline for solar in California?",
        a: "Systems in progress or completed before January 1, 2027 are excluded from a new-construction property tax reassessment. Systems excluded before that sunset keep the exclusion until the property changes ownership.",
      },
    ],
    cta: {
      title: "Get a straight number, not a 2024 slide deck",
      text: "Send us twelve months of SDG&E statements and we'll model what solar and storage actually do for your bill in 2026, incentives included and excluded honestly.",
    },
    ctaType: "solar",
  },
  {
    slug: "reroof-and-nem-2-grandfathering",
    title: "Does a Reroof End Your NEM 2.0 Grandfathering? | McKay",
    h1: "Does reroofing under existing solar panels end your NEM 2.0 grandfathering?",
    metaDescription:
      "A like-for-like reroof under solar panels does not touch your NEM 2.0 tariff. What does put it at risk is bundled equipment changes. Here's the tariff math and the permit rules.",
    keywords:
      "NEM 2.0 grandfathering, reroof under solar panels, NEM-ST SDG&E, solar panel removal and reinstallation, net metering San Diego, CSLB C-10 C-39",
    category: "Solar Insights",
    datePublished: "2026-09-02",
    dateModified: "2026-09-09",
    readMinutes: 9,
    hero: {
      src: solarInstallHero.url,
      alt: "Solar panels stacked and removed from a roof during a reroofing project",
      caption: "The panels come off, the roof gets replaced, the same panels go back on. Done right, none of that touches your NEM 2.0 tariff.",
    },
    intro: [
      "If your roof needs work and there is solar sitting on top of it, the first question out of most people's mouths is whether the reroof will cost them their NEM 2.0 grandfathering. It is a reasonable question. It is also, most of the time, aimed at the wrong thing. The fear should be pointed at the equipment list, not at the roofing work itself.",
      "This page explains what your grandfathering actually is, what the tariff rules literally say, where the real risk sits, and what we do on our end to keep the paperwork boring. Every tariff statement below carries a source and a last-verified date, because rules change. If you are reading this long after the verification date, check the source before you rely on it.",
      "We hold both sides of this job under one license, CSLB #1130793 with B, C-10, and C-39 classifications, which matters more than it sounds like it should. Keep reading and you'll see why.",
    ],
    tldr: [
      "NEM 2.0, called Schedule NEM-ST at SDG&E, guarantees a 20-year run from your original interconnection date, regardless of roof work, as long as the generating facility's capacity doesn't materially change.",
      "The tariff's headroom for changes is the greater of 10% of your original nameplate capacity or 1 kW. A true like-for-like panel removal and reinstallation uses none of it.",
      "The risk isn't the roof. It's bundling a system change, like adding panels, replacing a cracked module with a higher-rated one, or swapping the inverter, into the same project without tracking it against that threshold.",
      "San Diego's own permit bulletin requires the reinstallation to be performed by a contractor holding a C-10 or C-46 license, and it explicitly forbids altering the original equipment under that permit.",
    ],
    sections: [
      {
        id: "what-is-nem-2-grandfathering",
        h2: "What exactly is NEM 2.0 grandfathering?",
        blocks: [
          {
            type: "p",
            text: "If your solar was interconnected before April 15, 2023, you're on an older net metering tariff, and California guarantees you a defined run on it. The CPUC states it plainly: customer-generators are allowed to remain on the NEM 2.0 tariff for 20 years from the date they interconnected [(CPUC, Net Energy Metering and Net Billing)](https://www.cpuc.ca.gov/).",
          },
          {
            type: "p",
            text: "In San Diego, the tariff has a different name than the one everyone says out loud. SDG&E's NEM 2.0 is Schedule NEM-ST, Net Energy Metering Successor Tariff. SDG&E hit its NEM 1.0 cap on June 29, 2016, and interconnections after that went onto NEM-ST. Its own language: eligible customer-generators may remain on the schedule for 20 years from the original year of interconnection [(SDG&E Schedule NEM-ST)](https://www.sdge.com/).",
          },
          {
            type: "p",
            text: "Two things worth noticing. The clock runs from interconnection, not from the day the panels went on the roof and not from the day you signed. And it's tied to the generating facility, not to you personally, which is why the rules care so much about whether that facility has changed.",
          },
        ],
      },
      {
        id: "nem-3-comparison",
        h2: "Why does losing NEM 2.0 actually matter, versus NEM 3.0?",
        blocks: [
          {
            type: "p",
            text: "Since April 15, 2023, new interconnection applicants take service on the net billing tariff, informally NEM 3.0, adopted by the CPUC in Decision D.22-12-056. The practical difference for a homeowner is what an exported kilowatt-hour is worth. Under net billing, export credits are well below retail rates, which changes the arithmetic of a system that was designed and sold on the assumption that the grid would bank power at something close to retail.",
          },
          {
            type: "p",
            text: "That's why \"will this cost me my NEM 2.0\" is the first question out of people's mouths when we tell them their roof needs replacing. It's a reasonable question. It's also usually the wrong thing to be afraid of - the fear is typically pointed at the roof when it should be pointed at the equipment list. See our [solar cost breakdown for 2026](/post/solar-cost-san-diego-2026) for how NEM 3.0 pricing actually works.",
          },
        ],
      },
      {
        id: "what-the-tariff-actually-tests",
        h2: "What does the tariff actually test when equipment changes?",
        blocks: [
          {
            type: "p",
            text: "Here's the case we're talking about: same site, same modules, the exact ones that came off, same inverter, same string layout, same module count. New underlayment, new roof covering underneath. Nothing about the electrical system changed except that it spent two weeks in your garage.",
          },
          {
            type: "p",
            text: "The tariff test for keeping your transition period is written around capacity, not around whether the roof underneath got replaced. SDG&E's tariff language: generating facilities eligible for the 20-year transition period that are modified or repaired remain eligible for the remainder of the period as long as the modifications don't increase the facility by more than the greater of 10% of the original nameplate rating capacity, or 1 kW [(SDG&E Schedule NEM-ST)](https://www.sdge.com/).",
          },
          {
            type: "p",
            text: "SDG&E puts the same rule in homeowner language on its NEM page: systems 10 kW and smaller can be increased by no more than 1 kW; systems larger than 10 kW by no more than 10% of existing size. Exceed it, and SDG&E states the customer becomes responsible for non-bypassable charges on 100% of the net kWh delivered.",
          },
          {
            type: "ul",
            items: [
              "A 6 kW array: the threshold is the greater of 1 kW or 600 W, so 1 kW.",
              "A 12 kW array: the greater of 1 kW or 1.2 kW, so 1.2 kW.",
              "That headroom is your buffer. A true like-for-like reinstall consumes none of it.",
            ],
          },
          {
            type: "callout",
            title: "No guarantees, only good paperwork",
            text: "We are not telling you your grandfathering is guaranteed. We can't, and neither can any contractor who tells you otherwise. Eligibility is determined by SDG&E under rules set by the CPUC. What we can do is make sure the project we hand them doesn't give them a reason to look twice.",
          },
        ],
        image: {
          src: roofReplacementSection.url,
          alt: "New roof underlayment being installed beneath solar mounting hardware",
          caption: "New underlayment, new roof covering, same modules and inverter - the tariff test is about capacity, not roof materials.",
        },
      },
      {
        id: "how-this-actually-goes-wrong",
        h2: "How does a reroof actually put NEM 2.0 at risk?",
        blocks: [
          {
            type: "p",
            text: "Every case we've seen where a homeowner had a real problem, the problem was a system change bundled into a roof project, not the roof project itself.",
          },
          { type: "h3", text: "Adding panels while everything's already down" },
          {
            type: "p",
            text: "It's a completely natural thing to ask while an array is already on the ground, and it's the single fastest way to eat your 1 kW of headroom. Adding capacity is a legitimate decision, but it's a separate decision with its own consequences, and it should be made deliberately, with the threshold math in front of you, not as an upsell on reinstall day.",
          },
          { type: "h3", text: "A cracked module gets replaced with a different one" },
          {
            type: "p",
            text: "If a module cracks during removal, and anyone who says it never happens hasn't done enough of these, you can't usually buy the identical module you bought years ago. The replacement carries a different nameplate rating, often higher. Replace one module, nothing moves. Replace six on a small array and you can walk past 1 kW without anyone writing \"system expansion\" on a single document. This is why we photograph and log every module by serial before it comes off, and why we tell you before we order a replacement, not after.",
          },
          { type: "h3", text: "Swapping the inverter while everything's disconnected" },
          {
            type: "p",
            text: "SDG&E's own interconnection application lists Physical Changes to an Interconnected NEM or NEM-ST Generating Facility as its own application type, describing a request for modification due to changes like adding panels or changing inverters or turbines. The utility has a form for this. Filing it is not automatically bad. Filing it without knowing you filed it is bad.",
          },
          { type: "h3", text: "Adding a battery on the same invoice" },
          {
            type: "p",
            text: "Batteries are usually the right answer in SDG&E territory, since peak runs 4-9pm and self-consumption is where the value is now, not export credits. But a storage addition is a change to the interconnected facility and belongs in its own conversation with its own paperwork, not stapled to a roofing invoice. See our [solar battery installation](/services/solar-battery-installation) page if you want to talk about it deliberately.",
          },
          {
            type: "p",
            text: "Notice what isn't on this list: new shingles, new tile, new underlayment, new flashing, a fully rebuilt deck. None of that touches the nameplate capacity of your generating facility. See [roof replacement](/services/roof-replacement) for how that side of the job works.",
          },
        ],
      },
      {
        id: "permits-and-who-holds-them",
        h2: "What does the city actually require for a reroof under solar?",
        blocks: [
          {
            type: "p",
            text: "The City of San Diego addresses this case directly. Where existing roof-mounted PV is removed for renewal of the roof covering, the original approved record plan set must accompany the submitted PV plans to confirm the system was permitted and installed, and a pre-removal inspection of the existing system may be required.",
          },
          {
            type: "p",
            text: "The bulletin is explicit that the permit for reinstallation of the existing PV does not allow any alteration of the original equipment or system, and that the PV work for reroofing purposes shall be performed by a qualified contractor holding a C-10 or C-46 license [(City of San Diego Information Bulletin 301)](https://www.cslb.ca.gov/).",
          },
          {
            type: "p",
            text: "Read that last requirement twice, because it's the whole reason this job goes sideways when it's split between two companies. The roof needs a roofing classification. The panels need an electrical or solar classification. Somebody has to hold both, or you're coordinating two contractors who each think the other one owns the seam, and the seam is where the leak is.",
          },
          {
            type: "callout",
            title: "One license, no seam",
            text: "McKay holds both sides of that seam under one license. CSLB license #1130793 is current and active and carries B (General Building), C-10 (Electrical), and C-39 (Roofing) classifications, so the roofing work and the PV electrical work run under the same contractor.",
          },
        ],
      },
      {
        id: "what-to-do-right-now",
        h2: "What should I check right now if my panels are already off the roof?",
        blocks: [
          {
            type: "p",
            text: "If a roofer has your panels on the ground right now, or you're holding a proposal that bundles \"reroof plus panel reset\" with a line item you don't recognize, three things are worth doing today.",
          },
          {
            type: "ol",
            items: [
              "Get the proposal's equipment list and compare it to what's actually on your roof: module count, module model, inverter model. If any of those three changed, that's a system modification you want to know about before it's energized.",
              "Find your interconnection paperwork. Your permission-to-operate letter has your original nameplate capacity on it - that's the baseline the 10%-or-1-kW test is measured against.",
              "If something has already changed, call SDG&E rather than guessing. They administer the tariff. We don't, and neither does the company that did the work.",
            ],
          },
          {
            type: "p",
            text: "Send us your plan set and we'll read it before you sign anything. If your panels need to come off for a [roof inspection](/services/roof-inspection) or [roof replacement](/services/roof-replacement) first, or if you need a proper [solar panel removal and reinstallation](/services/solar-panel-removal-reinstallation), [book an assessment](/book/solar-repair) and we'll walk the math with you before any panel comes down.",
          },
        ],
      },
    ],
    faqs: [
      {
        q: "Does removing and reinstalling the same solar panels for a reroof cancel my NEM 2.0?",
        a: "No. A true like-for-like removal and reinstallation, same modules, same inverter, same module count, does not increase your system's nameplate capacity and does not affect your NEM-ST eligibility.",
      },
      {
        q: "How much can my system change before it risks my grandfathering?",
        a: "The threshold is the greater of 10% of your original nameplate capacity or 1 kW. For most residential systems under 10 kW, that means about 1 kW of headroom for any capacity increase.",
      },
      {
        q: "What happens if I exceed the capacity threshold?",
        a: "SDG&E's tariff states the customer becomes responsible for non-bypassable charges on 100% of net kWh delivered, or the facility may need to be metered separately or moved to another tariff entirely.",
      },
      {
        q: "Can any roofer take my panels off and put them back on?",
        a: "The City of San Diego's bulletin requires the PV work for reroofing purposes to be performed by a contractor holding a C-10 or C-46 license. A roofing-only contractor without that classification isn't permitted to do that part of the job.",
      },
      {
        q: "Should I add panels or a battery while my system is already down for the reroof?",
        a: "You can, but treat it as its own decision with its own paperwork and its own threshold math, not as a same-invoice upsell. Bundling it in without tracking capacity is how homeowners accidentally lose their transition period.",
      },
      {
        q: "How long does a reroof-under-solar project typically take?",
        a: "Panel removal, roof replacement, and reinstallation on a typical residential job usually runs one to two weeks depending on roof size and weather, plus permit processing time with the city.",
      },
    ],
    cta: {
      title: "Keep your NEM 2.0 while you fix the roof underneath it",
      text: "Send us your plan set and interconnection paperwork and we'll tell you honestly whether the reroof puts your tariff at risk, before anything comes off the roof.",
    },
    ctaType: "solar",
  },
  {
    slug: "electriq-power-customers",
    title: "Electriq Power Customers: What to Do Now | McKay",
    h1: "Electriq Power went out of business. What should PowerPod owners do now?",
    metaDescription:
      "Electriq Power filed Chapter 7 bankruptcy in May 2024 and liquidated. Here's what that means for your PowerPod battery, what a licensed contractor can and can't do, and your options.",
    keywords:
      "Electriq Power bankruptcy, PowerPod battery, Electriq Power customers, home battery replacement San Diego, Tesla Powerwall replacement, Enphase IQ battery",
    category: "Solar Service",
    datePublished: "2024-06-20",
    dateModified: "2026-09-09",
    readMinutes: 7,
    hero: {
      src: solarBatteryHero.url,
      alt: "Home battery storage unit mounted on a garage wall next to an electrical panel",
      caption: "Electriq Power's PowerPod batteries are still on plenty of San Diego walls. The company behind them is not.",
    },
    intro: [
      "Electriq Power, the California company behind the PowerPod home battery, filed for Chapter 7 bankruptcy on May 3, 2024 and liquidated its assets [(BusinessWire, Electriq Power Chapter 7 filing)](https://www.energy.gov/). Chapter 7 matters here: unlike some solar company bankruptcies where a successor picked up pieces of the business, liquidation means there is no company left to answer the phone, push a firmware update, or process a warranty claim [(Solar Power World, Electriq Power liquidation)](https://www.energy.gov/).",
      "If you own a PowerPod, nothing about your battery failed today. But the manufacturer behind it is gone, and that changes what happens the next time something does go wrong. This page is written for homeowners trying to figure out what that actually means and what their real options are.",
      "McKay Roofing and Solar is not affiliated with Electriq Power. This is general information, not legal advice. We're a licensed San Diego contractor, CSLB #1130793 with B, C-10, and C-39 classifications, and we're Tesla and Enphase Certified installers.",
    ],
    tldr: [
      "Electriq Power filed Chapter 7 bankruptcy on May 3, 2024 and liquidated. There is no company left to honor the PowerPod warranty.",
      "Your battery may keep working for years, but replacement parts are scarce and the monitoring app can go dark without notice.",
      "No contractor can revive a dead company's warranty. What a licensed C-10 electrician can do is test, diagnose, and if needed replace with a supported battery.",
      "Homeowners in some city-partnered Electriq programs, including cases reported in Santa Barbara County, were left sorting out liens - a separate issue from the battery hardware itself.",
    ],
    sections: [
      {
        id: "what-happened-to-electriq",
        h2: "What exactly happened to Electriq Power?",
        blocks: [
          {
            type: "p",
            text: "Electriq Power Holdings, founded in Silicon Valley in 2014, filed a voluntary petition for Chapter 7 bankruptcy on May 3, 2024 [(BusinessWire)](https://www.energy.gov/). By June 2024, the company had engaged an auction firm to liquidate more than $18 million in assets [(Solar Power World)](https://www.energy.gov/).",
          },
          {
            type: "p",
            text: "Chapter 7 is liquidation, not reorganization. There is no ongoing entity that continues to operate the business, honor warranties, or maintain the servers behind the PowerPod's monitoring app. That is the key difference from a Chapter 11 filing, where a company sometimes keeps operating while it restructures debt.",
          },
          {
            type: "p",
            text: "In some California markets, Electriq had partnered with city programs to install batteries, and homeowners in those arrangements were also left sorting out liens tied to the financing structure, a situation that made local news in Santa Barbara County. If you're in a similar program, that lien question is separate from the hardware question and worth a conversation with an attorney, not a contractor.",
          },
        ],
      },
      {
        id: "what-it-means-for-your-powerpod",
        h2: "What does this actually mean for my PowerPod battery?",
        blocks: [
          {
            type: "p",
            text: "Your battery may keep working for years. Lithium battery hardware doesn't stop functioning the day a manufacturer closes its doors. But as a practical matter, three things change:",
          },
          {
            type: "ul",
            items: [
              "The manufacturer warranty has nobody left to honor it. A written warranty is only as good as the company standing behind it.",
              "Replacement parts are scarce. If a component fails, sourcing an OEM part from a liquidated manufacturer is difficult or impossible.",
              "The monitoring app can go dark without notice, since there's no company maintaining the servers or pushing updates.",
            ],
          },
          {
            type: "callout",
            title: "Why this matters for your bill",
            text: "A battery that silently stops cycling also stops saving you money on SDG&E's 4pm-9pm peak rates, which is the whole reason it's on your wall in the first place. A dead app doesn't always mean a dead battery, but it means you've lost visibility into whether it's still doing its job.",
          },
        ],
        image: {
          src: teslaPowerwallSection.url,
          alt: "Electrician inspecting a wall-mounted home battery and its electrical connections",
          caption: "A health check tells you whether the hardware, the app, or both have stopped working.",
        },
      },
      {
        id: "what-a-contractor-can-and-cant-do",
        h2: "What can a licensed contractor actually do about it?",
        blocks: [
          {
            type: "p",
            text: "No contractor can revive a dead company's warranty, and you should be wary of anyone who claims otherwise. That promise isn't something a third party is able to make good on, regardless of license or certification.",
          },
          {
            type: "p",
            text: "What a licensed C-10 electrical contractor can do:",
          },
          {
            type: "ul",
            items: [
              "Test whether your battery is actually charging and discharging on schedule.",
              "Diagnose inverter and gateway faults.",
              "Assess whether a repair is realistic or the honest answer is replacement.",
              "Install a supported battery, [Tesla Powerwall](/services/tesla-powerwall-installation) or Enphase IQ, from manufacturers with deep, funded warranty programs, in many cases without affecting your existing net-metering status.",
            ],
          },
          {
            type: "p",
            text: "We confirm the specifics for your system first, since every PowerPod install has its own inverter pairing and panel configuration. If a replacement makes sense, $0-down lease and financing options are available through our partner network. See [solar battery installation](/services/solar-battery-installation) for how we approach a replacement project.",
          },
        ],
      },
      {
        id: "is-replacement-worth-it",
        h2: "Is replacing a dead-manufacturer battery actually worth it?",
        blocks: [
          {
            type: "p",
            text: "Under SDG&E's evening peak pricing, storage usually carries the savings, not the panels alone. We'll model that from your bills, not a brochure. California's property tax exclusion for completed solar-plus-storage runs through January 1, 2027, which is also worth checking against your timeline if you're considering an upgrade rather than a like-for-like swap.",
          },
          {
            type: "p",
            text: "If your PowerPod is still functioning and monitoring correctly, there may be no urgency at all. The right move is often just a periodic health check rather than an immediate replacement. If it's already showing faults, a quiet app, or a battery that isn't cycling on schedule, that's when the replacement conversation becomes worth having. Either way, start with a [roof inspection](/services/roof-inspection) if your solar and battery share mounting infrastructure that hasn't been checked recently.",
          },
        ],
      },
      {
        id: "next-steps",
        h2: "What should I do next if I own a PowerPod?",
        blocks: [
          {
            type: "ol",
            items: [
              "Check whether the monitoring app is still updating. If it's gone dark, that alone doesn't mean the battery is dead.",
              "Have a licensed electrician run a health check on charge and discharge cycles, not just a visual inspection.",
              "If you're in a city-partnered Electriq program with a lien, talk to an attorney about that separately from the hardware question.",
              "If replacement is the honest answer, compare Tesla Powerwall and Enphase IQ options and ask about financing before committing.",
              "Keep your existing solar system's paperwork handy - your net-metering tariff status matters when swapping battery hardware.",
            ],
          },
          {
            type: "p",
            text: "Estimates are always free. [Contact us](/contact) or call to get a battery health check scheduled, and if you decide replacement makes sense, we'll walk you through [solar battery installation](/services/solar-battery-installation) options that come with a manufacturer that's actually still in business.",
          },
        ],
      },
    ],
    faqs: [
      {
        q: "My PowerPod app stopped updating. Is the battery dead?",
        a: "Not necessarily. With no company behind the app, monitoring can fail while the hardware still works, or vice versa. A health check from a licensed electrician tells you which it is.",
      },
      {
        q: "Can I get warranty service from Electriq Power?",
        a: "No. Electriq Power filed Chapter 7 bankruptcy on May 3, 2024 and liquidated its assets. There is no company left to process a warranty claim.",
      },
      {
        q: "Is a replacement battery worth it?",
        a: "Under SDG&E's evening peak pricing, storage usually carries the savings. We model that from your actual bills rather than a brochure number, and check whether the property tax exclusion deadline of January 1, 2027 affects your timing.",
      },
      {
        q: "Will replacing my PowerPod affect my solar net-metering status?",
        a: "In many cases a battery replacement can be done without affecting your existing net-metering tariff, but we confirm the specifics of your system, inverter pairing, and interconnection paperwork before doing any work.",
      },
      {
        q: "Are Tesla Powerwall and Enphase IQ good replacements for a PowerPod?",
        a: "They're from manufacturers with active, funded warranty programs, which is the main gap a PowerPod now has. We're Tesla and Enphase Certified installers and can walk you through which fits your existing solar setup.",
      },
      {
        q: "I'm in a city program that financed my Electriq battery. What about the lien?",
        a: "That's a separate legal and financial question from the hardware itself. We recommend talking to an attorney about lien obligations tied to a liquidated manufacturer's financing program.",
      },
    ],
    cta: {
      title: "Get your PowerPod checked before it fails silently",
      text: "We're not affiliated with Electriq Power, but we can test your battery, tell you honestly whether it's still doing its job, and walk you through a supported replacement if it isn't.",
    },
    ctaType: "solar",
  },
];
